import React from "react";
import { Button } from "./ui/button";

const CHUNK_RELOAD_KEY = "wd-chunk-reload";

function isChunkLoadError(error) {
  const name = error?.name || "";
  const message = error?.message || String(error || "");
  return (
    name === "ChunkLoadError" ||
    /Loading CSS chunk \d+ failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message)
  );
}

/**
 * Render/chunk errors otherwise unmount the whole admin tree (white screen).
 * Sidebar stays if this wraps only the page outlet; app-level wrap covers the rest.
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  componentDidCatch(error, info) {
    console.error("RouteErrorBoundary:", error, info?.componentStack);
    if (!isChunkLoadError(error)) return;
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
    } catch (e) {
      /* ignore */
    }
  }

  retry = () => {
    this.setState({ error: null });
    if (typeof this.props.onRetry === "function") this.props.onRetry();
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="flex flex-1 min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center"
        role="alert"
        data-testid="route-error-boundary"
      >
        <p className="text-base font-semibold text-foreground">
          This page failed to load
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Switch tabs again or reload. If it keeps happening, hard-refresh the
          browser so it picks up the latest app files.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          <Button variant="outline" onClick={this.retry}>
            Try again
          </Button>
          <Button onClick={this.reload}>Reload</Button>
        </div>
      </div>
    );
  }
}
